import './Targeting.scss';

import React, { useMemo } from 'react';

import { getPublisherTargetLineItem } from '../../../../../../../../../common/adTargeting';
import { useFieldArray, useFieldFast } from '../../../../../../../../../common/Form/hooks';
import type { TargetingScope } from '../../../../../../../../../common/Targeting';
import { TargetingPanel } from '../../../../../../../../../common/Targeting';
import type {
  TargetingPanelAlerts,
  TargetingPanelHeader,
} from '../../../../../../../../../common/Targeting/TargetingPanel/TargetingPanel';
import type { TargetingTableRowGroup } from '../../../../../../../../../common/TargetingTable/TargetingTable';
import type { LineItem, TargetingTermValue, TargetingValue } from '../../../../../../../../../models';
import bem from '../../../../../../../../../utils/bem';
import { getPublisherTargetingList } from '../../../../../../../TraffickingPage/columns';
import { EntityTypeArray } from '../../../../../../../TraffickingPage/hooks/makePageDrawerPlugin';
import type { AdEntity } from '../../../../../types';
import {
  computeMismatchedCountries,
  computeMismatchedPublishers,
  generateMultiEditTargetingTableData,
  getTargetingHeaderText,
  getUniqueAdTypeKeys,
  unionAdCountriesTarget,
  unionAdLineItemCountries,
  unionAdPublishers,
  unionLineItemCountries,
  unionLineItemPublishers,
} from './utils';

const MISMATCHED_TARGETING_WARNING =
  'Some content targets may not be available across all publishers and countries. Check content availability before applying on cross-pub and cross-country lines.';

const [body, element] = bem('multiple-edit-targeting-table');

// Module-scoped sentinel so that a `useFieldFast` field whose value is
// transiently `undefined` (e.g. before initialization) doesn't churn a fresh
// `[]` reference into eight downstream `useMemo`s on every render.
const EMPTY_ENTITIES: ReadonlyArray<LineItem | AdEntity> = [];

type TargetingProps = {
  entityType: EntityTypeArray;
};

type EntityDerivations = {
  lineItemCountryIds: string[] | undefined;
  countryIdsForFilteringValues: string[] | undefined;
  publisherIdsUnion: string[];
  publishersUnion: TargetingValue[] | undefined;
  adTypes: ReturnType<typeof getUniqueAdTypeKeys>;
  anyAdHasInvalidCountry: boolean;
  anyAdHasInvalidPublisher: boolean;
  entitiesHaveMismatchedCountries: boolean;
  entitiesHaveMismatchedPublishers: boolean;
};

export default function Targeting({ entityType }: TargetingProps): JSX.Element {
  const isLineItem = entityType === EntityTypeArray.LINE_ITEM_ARRAY;

  const [
    ,
    { value: targetingTermValues },
    { insertAt, removeAt, replaceAt, removeAll },
  ] = useFieldArray<TargetingTermValue>('targetingTermValues');
  const [, { value: entitiesRaw }] = useFieldFast<LineItem[] | AdEntity[]>('entities');
  const entities = entitiesRaw ?? EMPTY_ENTITIES;

  const data: TargetingTableRowGroup[] = useMemo(
    () => generateMultiEditTargetingTableData(targetingTermValues, isLineItem),
    [targetingTermValues, isLineItem]
  );

  // All read-only unions describing the "scope" within which targeting is
  // being chosen across the selected entities. See `TargetingScope` for the
  // contract each field satisfies.
  // Batched to reduce re-renders.
  const entityDerivations = useMemo<EntityDerivations>(() => {
    const lineItemCountryUnion = isLineItem
      ? unionLineItemCountries(entities as LineItem[])
      : unionAdLineItemCountries(entities as AdEntity[]);
    // Ads' own targeted countries differ from their parent line items' country
    // union; for line items the two are the same and we reuse the array above
    // to avoid a redundant traversal of `entities`.
    const filterCountryUnion = isLineItem ? lineItemCountryUnion : unionAdCountriesTarget(entities as AdEntity[]);
    const publisherIdsUnion = isLineItem
      ? unionLineItemPublishers(entities as LineItem[])
      : unionAdPublishers(entities as AdEntity[]);

    return {
      lineItemCountryIds: lineItemCountryUnion.length ? lineItemCountryUnion : undefined,
      countryIdsForFilteringValues: filterCountryUnion.length ? filterCountryUnion : undefined,
      publisherIdsUnion,
      publishersUnion: publisherIdsUnion.length ? getPublisherTargetLineItem(publisherIdsUnion) : undefined,
      adTypes: isLineItem ? [] : getUniqueAdTypeKeys(entities as AdEntity[]),
      // `hasInvalidCountry` / `hasInvalidPublisher` only exist on ads -- line
      // items do not have those fields, so the line-item branch short-circuits
      // to false rather than calling `.some` on an irrelevant type.
      anyAdHasInvalidCountry: !isLineItem && (entities as AdEntity[]).some((ad) => ad.hasInvalidCountry),
      anyAdHasInvalidPublisher: !isLineItem && (entities as AdEntity[]).some((ad) => ad.hasInvalidPublisher),
      entitiesHaveMismatchedCountries: isLineItem
        ? computeMismatchedCountries(entities as LineItem[], false)
        : computeMismatchedCountries(entities as AdEntity[], true),
      entitiesHaveMismatchedPublishers: isLineItem
        ? computeMismatchedPublishers(entities as LineItem[], false)
        : computeMismatchedPublishers(entities as AdEntity[], true),
    };
  }, [isLineItem, entities]);

  const {
    lineItemCountryIds,
    countryIdsForFilteringValues,
    publisherIdsUnion,
    publishersUnion,
    adTypes,
    anyAdHasInvalidCountry,
    anyAdHasInvalidPublisher,
    entitiesHaveMismatchedCountries,
    entitiesHaveMismatchedPublishers,
  } = entityDerivations;

  const showMismatchedTargetingWarning = entitiesHaveMismatchedCountries || entitiesHaveMismatchedPublishers;

  const scope: TargetingScope = useMemo(
    () => ({
      isAdEntity: !isLineItem,
      lineItemCountryIds,
      countryIdsForFilteringValues,
      lineItemPublishers: publishersUnion,
      publishersForFilteringValues: publishersUnion,
      adTypes,
      allowEditCountryTarget: !isLineItem && (anyAdHasInvalidCountry || (lineItemCountryIds?.length ?? 0) > 1),
      entitiesHaveMismatchedCountries,
    }),
    [
      isLineItem,
      lineItemCountryIds,
      countryIdsForFilteringValues,
      publishersUnion,
      adTypes,
      anyAdHasInvalidCountry,
      entitiesHaveMismatchedCountries,
    ]
  );

  // Header config for the multi-edit card. Labels follow the same convention
  // used elsewhere in the app:
  //   - Line item multi-edit: "Country: " / "Publisher: " (the country and
  //     publisher being shown ARE the line items' own values).
  //   - Ad multi-edit:        "Line Country: " / "Line Publisher: " (the
  //     country and publisher belong to the ads' parent line items, distinct
  //     from each ad's own targeting -- mirrors DrawerTargetingTable for ads).
  //
  // The view-toggle area is suppressed because we deliberately do NOT pass a
  // `textViewer` config: the multi-edit card has no text-view alternative.
  // TargetingPanel/TargetingHeader handle the "no toggle when no textViewer"
  // case directly (see `TargetingHeader.tsx` and the panel tests).
  const headerConfig: TargetingPanelHeader = useMemo(
    () => ({
      countryLabel: isLineItem ? 'Country: ' : 'Line Country: ',
      publisherLabel: isLineItem ? 'Publisher: ' : 'Line Publisher: ',
      lineItemCountryIds,
      lineItemPublishers: getPublisherTargetingList({ value: publisherIdsUnion }) || undefined,
      classNames: {
        country: element('header-country'),
        publisher: element('header-publisher'),
      },
    }),
    [isLineItem, lineItemCountryIds, publisherIdsUnion]
  );

  // Alerts config -- only relevant for the ad multi-edit because line items
  // don't have invalid-publisher / invalid-country fields. Passing `undefined`
  // on the line item side keeps the alerts off entirely. The ad-sequence info
  // alert is intentionally omitted from multi-edit because plumbing
  // `adSequence` through `AdEntity` / the GraphQL fragment / `getMultipleEdit
  // DrawerAdsInitialValues` is more invasive than the user wanted for this
  // refactor.
  const alertsConfig: TargetingPanelAlerts | undefined = useMemo(() => {
    if (isLineItem) return undefined;
    return {
      hasInvalidPublisher: anyAdHasInvalidPublisher,
      hasInvalidCountry: anyAdHasInvalidCountry,
      classNames: {
        invalidPublisher: element('invalid-publisher-alert'),
        invalidCountry: element('invalid-country-alert'),
      },
    };
  }, [isLineItem, anyAdHasInvalidPublisher, anyAdHasInvalidCountry]);

  const tableListHelpers = useMemo(() => ({ removeAt, replaceAt, removeAll }), [removeAt, replaceAt, removeAll]);

  const dropdownsConfig = useMemo(
    () => ({
      scope,
      targetings: targetingTermValues,
      listHelpers: { insertAt, replaceAt, removeAt },
    }),
    [scope, targetingTermValues, insertAt, replaceAt, removeAt]
  );

  return (
    <div className={body()}>
      <div className={element('header')}>{getTargetingHeaderText(entityType)}</div>
      {showMismatchedTargetingWarning && <div className={element('warning')}>{MISMATCHED_TARGETING_WARNING}</div>}
      <TargetingPanel
        data={data}
        listHelpers={tableListHelpers}
        isTableToggleable
        editingEntityType={isLineItem ? 'lineItem' : 'ad'}
        header={headerConfig}
        alerts={alertsConfig}
        dropdowns={dropdownsConfig}
      />
    </div>
  );
}
